import { useEffect, useRef, useState } from "react";
import { Eye, EyeOff, Loader2, Plus, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { openPdf } from "@/lib/pdf";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { FaqAdmin } from "@/components/admin/FaqAdmin";
import { DocumentOrder } from "@/components/admin/DocumentOrder";

const btn = "inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-4 py-2 transition-colors disabled:opacity-60";
const NEW = { id: null, name: "", description: "", in_email: false, template: "# Nazwa dokumentu\n\nDziałając w imieniu {nazwa firmy}, NIP: {NIP} …\n\n{pole do wypełnienia przez klienta}\n\nWarszawa, {data bieżąca} r.\n", enabled: true };

function DocEditor({ doc, placeholders, onSaved, onDeleted }) {
  const [form, setForm] = useState(doc);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [live, setLive] = useState(false);
  const [liveUrl, setLiveUrl] = useState("");
  const [liveBusy, setLiveBusy] = useState(false);
  const liveUrlRef = useRef("");
  useEffect(() => setForm(doc), [doc]);

  useEffect(() => {
    if (!live) return undefined;
    setLiveBusy(true);
    const timer = setTimeout(async () => {
      try {
        const r = await api.post("/admin/documents/preview", { name: form.name || "Podgląd", template: form.template, enabled: true }, { responseType: "blob" });
        if (liveUrlRef.current) URL.revokeObjectURL(liveUrlRef.current);
        liveUrlRef.current = URL.createObjectURL(r.data);
        setLiveUrl(liveUrlRef.current);
      } catch {
        toast.error("Nie udało się odświeżyć podglądu.");
      } finally {
        setLiveBusy(false);
      }
    }, 700);
    return () => clearTimeout(timer);
  }, [live, form.template, form.name]);

  useEffect(() => () => { if (liveUrlRef.current) URL.revokeObjectURL(liveUrlRef.current); }, []);
  const dirty = JSON.stringify(form) !== JSON.stringify(doc);
  const custom = [...new Set((form.template.match(/\{[^{}]+\}/g) || []).filter((k) => !placeholders.some((p) => p.key === k)))];

  const save = async () => {
    setSaving(true);
    try {
      const payload = { name: form.name, template: form.template, enabled: form.enabled, description: form.description || "", in_email: !!form.in_email && !form.internal, internal: !!form.internal };
      if (form.id) await api.put(`/admin/documents/${form.id}`, payload);
      else await api.post("/admin/documents", payload);
      toast.success("Dokument zapisany.");
      onSaved();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Nie udało się zapisać dokumentu.");
    } finally {
      setSaving(false);
    }
  };
  const preview = async () => {
    setPreviewing(true);
    try {
      await openPdf({ method: "post", url: "/admin/documents/preview", data: { name: form.name || "Podgląd", template: form.template, enabled: true } });
    } catch {
      toast.error("Nie udało się wygenerować podglądu.");
    } finally {
      setPreviewing(false);
    }
  };
  const remove = async () => {
    if (!window.confirm(`Usunąć dokument „${form.name}”?`)) return;
    await api.delete(`/admin/documents/${form.id}`);
    toast.success("Dokument usunięty.");
    onDeleted();
  };

  return (
    <div data-testid={`document-editor-${form.id || "new"}`} className="bg-card border border-border rounded-2xl p-5 sm:p-6 space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <Input data-testid="document-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nazwa dokumentu (widoczna w e-mailu i na stronie)" className="flex-1 min-w-[240px] bg-background" />
        <label className="flex items-center gap-2 text-xs font-semibold">
          <Switch data-testid="document-enabled-switch" checked={form.enabled} onCheckedChange={(v) => setForm({ ...form, enabled: v })} />
          {form.enabled ? (form.internal ? "Aktywny (strefa pracownika)" : "Aktywny (na stronie)") : "Ukryty"}
        </label>
        <label className="flex items-center gap-2 text-xs font-semibold">
          <Switch data-testid="document-internal-switch" checked={!!form.internal} onCheckedChange={(v) => setForm({ ...form, internal: v, in_email: v ? false : form.in_email })} />
          Wewnętrzny (tylko pracownicy)
        </label>
        {!form.internal && (
        <label className="flex items-center gap-2 text-xs font-semibold">
          <Switch data-testid="document-in-email-switch" checked={!!form.in_email} onCheckedChange={(v) => setForm({ ...form, in_email: v })} />
          W e-mailu z umową
        </label>
        )}
      </div>
      <Input data-testid="document-description-input" value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} maxLength={300} placeholder="Krótki opis „kiedy się przydaje” — widoczny na liście dokumentów dla klienta" className="bg-background" />
      <div className={`grid gap-4 ${live ? "lg:grid-cols-2" : ""}`}>
        <Textarea data-testid="document-template-textarea" value={form.template} onChange={(e) => setForm({ ...form, template: e.target.value })} spellCheck={false} className={`font-mono text-xs leading-relaxed bg-background ${live ? "min-h-[560px]" : "min-h-[280px]"}`} />
        {live && (
          <div data-testid="document-live-preview" className="relative rounded-xl border border-border bg-slate-100 dark:bg-slate-900 overflow-hidden min-h-[560px]">
            {liveUrl && <iframe title="Podgląd PDF" data-testid="document-live-preview-frame" src={`${liveUrl}#toolbar=0&view=FitH`} className="w-full h-full min-h-[560px]" />}
            {liveBusy && <div data-testid="document-live-preview-busy" className="absolute top-2 right-2 inline-flex items-center gap-1.5 text-[11px] font-semibold bg-background/90 border border-border rounded-full px-2.5 py-1"><Loader2 size={11} className="animate-spin" /> odświeżam…</div>}
          </div>
        )}
      </div>
      <p className="text-[11px] text-muted-foreground leading-relaxed">
        Pola klienta (wypełnia w formularzu przed pobraniem):{" "}
        {custom.length ? custom.map((k) => <code key={k} className="font-mono text-foreground mr-2">{k}</code>) : <em>brak — wszystkie pola uzupełnią się z zamówienia</em>}
      </p>
      <div className="flex flex-wrap items-center justify-between gap-3">
        {form.id ? (
          <button type="button" data-testid="document-delete-button" onClick={remove} className={`${btn} text-destructive hover:bg-destructive/10`}><Trash2 size={13} /> Usuń</button>
        ) : <span />}
        <div className="flex items-center gap-3">
          {dirty && <span className="text-xs text-brand-yellow">Niezapisane zmiany</span>}
          <button type="button" data-testid="document-live-toggle" onClick={() => setLive((v) => !v)} className={`${btn} border ${live ? "border-brand-teal text-brand-teal bg-brand-teal/5" : "border-border hover:bg-muted"}`}>
            {live ? <EyeOff size={13} /> : <Eye size={13} />} {live ? "Ukryj podgląd na żywo" : "Podgląd na żywo"}
          </button>
          <button type="button" data-testid="document-preview-button" onClick={preview} disabled={previewing} className={`${btn} border border-border hover:bg-muted`}>
            {previewing ? <Loader2 size={13} className="animate-spin" /> : <Eye size={13} />} Otwórz PDF
          </button>
          <button type="button" data-testid="document-save-button" onClick={save} disabled={saving || (!dirty && form.id)} className={`${btn} bg-brand-teal text-white hover:bg-brand-tealDark`}>
            {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Zapisz
          </button>
        </div>
      </div>
    </div>
  );
}

export function DocumentsAdmin() {
  const [data, setData] = useState(null);
  const [adding, setAdding] = useState(false);
  const load = () => api.get("/admin/documents").then((r) => { setData(r.data); setAdding(false); }).catch(() => toast.error("Nie udało się pobrać dokumentów."));
  useEffect(() => { load(); }, []);
  if (!data) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-slate-600" /></div>;

  return (
    <div data-testid="documents-admin" className="space-y-6">
      <div className="bg-card border border-border rounded-2xl p-5 sm:p-6">
        <h2 className="font-display font-bold text-lg">Generator dokumentów</h2>
        <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
          Aktywne dokumenty są widoczne na stronie /dokumenty; te z włączonym „W e-mailu z umową” dostają też przycisk w e-mailach do klienta i przy zamówieniu w panelu.
          Pola z zamówienia uzupełniają się automatycznie; inne pola w nawiasach klamrowych klient wypełnia sam na stronie przed pobraniem PDF.
          Markup jak w szablonie umowy: <code className="font-mono">#</code> tytuł, <code className="font-mono">##</code> nagłówek, <code className="font-mono">**bold**</code>, <code className="font-mono">1.</code>/<code className="font-mono">a.</code> listy, <code className="font-mono">[[SIGNATURES]]</code>, <code className="font-mono">&gt; </code> tekst do prawej. Pole z opcjami do wyboru: <code className="font-mono">{"{etykieta|opcja A|opcja B: *|opcja C #}"}</code> — <code className="font-mono">*</code> = dodatkowe pole tekstowe, <code className="font-mono">#</code> = kalendarz, <code className="font-mono">+</code> = lista kilku pozycji. Lista osób/pozycji z podpolami: <code className="font-mono">{"{osoby upoważnione+|imię i nazwisko|nr dokumentu tożsamości}"}</code> (klient dodaje kolejne pozycje przyciskiem). Pole zależne od wyboru: dopisz <code className="font-mono">@etykieta pola wyboru:numer opcji</code>, np. <code className="font-mono">{"{osoby?=+ @zakres odwołania:2|imię i nazwisko|nr dokumentu}"}</code> — pole pokaże się tylko po zaznaczeniu 2. opcji. Pole nieobowiązkowe: <code className="font-mono">{"{nowy telefon?=bez zmian}"}</code> (tekst po <code className="font-mono">?=</code> trafia do PDF, gdy puste). Inna etykieta pola z zamówienia: osobna linia <code className="font-mono">{"[[LABEL {nazwa firmy} = Dotychczasowa firma spółki]]"}</code>. Dane z REGON (GUS): gdy szablon zawiera <code className="font-mono">{"{NIP}"}</code>, po wpisaniu NIP-u automatycznie uzupełnią się <code className="font-mono">{"{nazwa firmy}"}</code>, <code className="font-mono">{"{adres}"}</code>, <code className="font-mono">{"{KRS}"}</code>, <code className="font-mono">{"{REGON}"}</code> i <code className="font-mono">{"{forma prawna}"}</code> — także w dokumentach wewnętrznych.
        </p>
        <div data-testid="documents-placeholders" className="mt-4 flex flex-wrap gap-2">
          {data.placeholders.map((p) => (
            <span key={p.key} title={p.label} className="font-mono text-[11px] px-2.5 py-1 rounded-full bg-slate-100 dark:bg-slate-800">{p.key}</span>
          ))}
        </div>
      </div>
      {data.documents.length > 1 && <DocumentOrder documents={data.documents} onChanged={load} />}
      {data.documents.map((d) => <DocEditor key={d.id} doc={d} placeholders={data.placeholders} onSaved={load} onDeleted={load} />)}
      {adding ? (
        <DocEditor doc={NEW} placeholders={data.placeholders} onSaved={load} onDeleted={load} />
      ) : (
        <button type="button" data-testid="document-add-button" onClick={() => setAdding(true)} className={`${btn} border border-dashed border-border hover:bg-muted w-full justify-center py-4`}>
          <Plus size={14} /> Dodaj nowy dokument
        </button>
      )}
      <FaqAdmin />
    </div>
  );
}
